import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "../../lib/api";
import { FileText, Download, Trash2, Upload } from "lucide-react";
import { toast, Toaster } from "sonner";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt";

export default function MesDocuments() {
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [docType, setDocType] = useState("bulletin");
    const fileRef = useRef(null);

    const load = () =>
        api.get("/me/documents")
            .then((r) => setDocs(r.data))
            .catch(() => toast.error("Erreur de chargement"));

    useEffect(() => { load(); }, []);

    const handleUpload = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (max 10 Mo)");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(
                `/me/documents/upload?doc_type=${docType}&title=${encodeURIComponent(file.name)}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success("Document envoyé");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur d'envoi");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) handleUpload(f);
    };

    const remove = async (id, uploadedByClient) => {
        if (!uploadedByClient) {
            toast.error("Vous ne pouvez supprimer que vos propres dépôts.");
            return;
        }
        if (!window.confirm("Supprimer ce document ?")) return;
        try {
            await api.delete(`/documents/${id}`);
            toast.success("Document supprimé");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const downloadUrl = (doc) => {
        const token = localStorage.getItem("vc_access_token");
        return `${API_BASE}/documents/${doc.id}/download?auth=${encodeURIComponent(token || "")}`;
    };

    const mine = docs.filter((d) => d.uploaded_by === "client");
    const shared = docs.filter((d) => d.uploaded_by !== "client");

    return (
        <div data-testid="client-page-docs" className="space-y-8 max-w-4xl">
            <Toaster position="top-center" theme="dark" richColors />
            <div>
                <span className="text-xs tracking-[0.3em] text-ice uppercase">— Mes documents</span>
                <h1 className="font-serif text-4xl mt-2">Documents</h1>
            </div>

            {/* Upload zone */}
            <section
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="bg-ink-600 border-2 border-dashed border-ice/30 rounded-xl p-8 text-center hover:border-ice/60 transition-colors"
            >
                <Upload size={28} className="text-ice mx-auto" />
                <h2 className="font-serif text-2xl mt-3">Déposer un document</h2>
                <p className="text-sm text-white/60 mt-2 max-w-md mx-auto">
                    Bulletin, CV, lettre de motivation, projets — glissez-déposez ou choisissez un fichier (PDF,
                    DOCX, JPG, PNG, max 10 Mo).
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <select
                        value={docType}
                        onChange={(e) => setDocType(e.target.value)}
                        data-testid="client-upload-type"
                        className="bg-ink-800 border border-white/15 text-white rounded-md px-3 py-2 text-sm focus:border-ice outline-none"
                    >
                        <option value="bulletin">Bulletin</option>
                        <option value="cv">CV</option>
                        <option value="lettre">Lettre de motivation</option>
                        <option value="autre">Autre</option>
                    </select>
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="client-upload-btn"
                        className="inline-flex items-center gap-2 bg-ice text-ink-900 hover:bg-white disabled:opacity-60 px-5 py-2.5 rounded-md text-sm font-medium transition-colors"
                    >
                        <Upload size={15} /> {uploading ? "Envoi en cours…" : "Choisir un fichier"}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept={ACCEPT}
                        data-testid="client-upload-input"
                        className="hidden"
                        onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                </div>
            </section>

            {/* My uploads */}
            <section>
                <h3 className="font-serif text-2xl mb-4">Mes dépôts</h3>
                <DocList docs={mine} onDelete={(d) => remove(d.id, true)} downloadUrl={downloadUrl} emptyText="Vous n'avez encore rien déposé." canDelete />
            </section>

            {/* Shared by consultant */}
            <section>
                <h3 className="font-serif text-2xl mb-4">Partagés par votre conseillère</h3>
                <DocList docs={shared} downloadUrl={downloadUrl} emptyText="Aucun document partagé pour le moment." />
            </section>
        </div>
    );
}

function DocList({ docs, onDelete, downloadUrl, emptyText, canDelete }) {
    if (docs.length === 0)
        return <div className="text-sm text-white/50 bg-ink-600 border border-white/10 rounded-xl p-6">{emptyText}</div>;
    return (
        <div className="bg-ink-600 border border-white/10 rounded-xl overflow-hidden">
            {docs.map((d) => (
                <div
                    key={d.id}
                    data-testid={`doc-row-${d.id}`}
                    className="flex items-center gap-3 p-4 border-b border-white/5 last:border-0"
                >
                    <FileText size={18} className="text-ice shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{d.title}</div>
                        <div className="text-xs text-white/50 mt-0.5">
                            {d.type.replace("_", " ")} · {new Date(d.created_at).toLocaleDateString("fr-FR")}
                            {d.size ? ` · ${(d.size / 1024).toFixed(1)} Ko` : ""}
                        </div>
                    </div>
                    {d.storage_path && (
                        <a
                            href={downloadUrl(d)}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 text-ice hover:text-white transition-colors"
                            title="Télécharger"
                        >
                            <Download size={16} />
                        </a>
                    )}
                    {canDelete && onDelete && (
                        <button
                            onClick={() => onDelete(d)}
                            className="p-2 text-white/40 hover:text-red-400 transition-colors"
                            title="Supprimer"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
